import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import {
  CHARGER_IDENTITY_RE, CHARGER_CONNECTIVITY_LINKS, CHARGER_CONNECTIVITY_HINTS, CHARGER_CONNECTIVITY_LABELS, connectivityIncludes, connectivityWith,
  type ChargerConnectivity,
} from '@evi/shared';
import { ShopTurnstile, hasTurnstile } from '@/components/ShopTurnstile';
import {
  validateSubmissionInvite,
  submitNetworkSubmissionPublic,
  type SubmissionInviteCheck,
  type PublicNetworkSubmission,
} from '@/lib/api';
import { prefillConnectivity } from './invitedSubmissionPrefill';
import styles from './SalesPartnerPage.module.css';

/**
 * Invite-only "Register a location" page (evinitiative.shop/submit/:token).
 * The ONLY external submission door after the public-form hardening: the
 * form renders solely against a live admin-issued invite; invalid, used,
 * expired, or revoked links get honest copy and no form. The recipient
 * email is read-only (the server forces it from the invite anyway), and
 * needs_info re-invites arrive pre-filled with the original submission —
 * submitting reopens THAT submission rather than creating a new one. The
 * prefill includes the recorded connectivity choice when there is one.
 * Turnstile stays as bot belt-and-suspenders on top of the token gate.
 */

const ROLES = [
  { value: 'electrician', label: 'Electrician / installer' },
  { value: 'sales_partner', label: 'EVI Sales Partner' },
  { value: 'fleet_manager', label: 'Fleet manager' },
  { value: 'property_manager', label: 'Property manager' },
  { value: 'other', label: 'Other' },
] as const;

const LOCATION_TYPES = [
  { value: 'commercial', label: 'Workplace / Commercial' },
  { value: 'multifamily', label: 'Multifamily building' },
  { value: 'public', label: 'Public site' },
  { value: 'fleet', label: 'Fleet depot' },
] as const;

const OCPP_WS_BASE =
  (import.meta.env['VITE_OCPP_WS_BASE'] as string | undefined) ?? 'wss://ocpp.stg.evinitiative.com';

/** When a charger goes live (Warren, 2026-10-01). Connecting alone never launches it. */
const GO_LIVE = 'A charger goes live after it connects, passes its plug test, and EV Initiative approves the launch.';

/** Required per deployment (Warren, 2026-10-01): one choice for every unit. */
const CONNECTIVITY_LABEL = 'How the chargers connect';

type UnitRow = { identity: string; vendor: string; model: string; connector: string; maxKw: string };
const EMPTY_UNIT: UnitRow = { identity: '', vendor: '', model: '', connector: 'J1772', maxKw: '' };

const DEAD_LINK_COPY: Record<string, { title: string; body: string }> = {
  used: {
    title: 'This link was already used.',
    body: 'Each invite works exactly once. If you need to change what you submitted, reply to your invite email or write support@evinitiative.com and we will send a fresh link.',
  },
  expired: {
    title: 'This link has expired.',
    body: 'Invites are good for 14 days. Reply to your invite email or write support@evinitiative.com and we will send a fresh one.',
  },
  revoked: {
    title: 'This link is no longer active.',
    body: 'The invite was withdrawn. If you think that is a mistake, write support@evinitiative.com.',
  },
  invalid: {
    title: 'This link is not valid.',
    body: 'Registering a location on the EV Initiative network is by invitation. Write support@evinitiative.com or ask your EV Initiative contact for an invite.',
  },
};

export function InvitedSubmissionPage() {
  const { token: inviteToken } = useParams<{ token: string }>();
  const [check, setCheck] = useState<SubmissionInviteCheck | 'loading' | 'error'>('loading');

  const [contact, setContact] = useState({ name: '', email: '', phone: '', role: '' });
  const [loc, setLoc] = useState({ name: '', street: '', city: '', state: '', zip: '', type: '' });
  const [units, setUnits] = useState<UnitRow[]>([{ ...EMPTY_UNIT }]);
  const [connectivity, setConnectivity] = useState<ChargerConnectivity | ''>('');
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [copied, setCopied] = useState(false);
  const tsRef = useRef<TurnstileInstance>(null);

  useEffect(() => {
    let cancelled = false;
    if (!inviteToken) {
      setCheck({ valid: false, reason: 'invalid' });
      return;
    }
    validateSubmissionInvite(inviteToken)
      .then((res) => {
        if (cancelled) return;
        setCheck(res);
        if (res.valid) {
          const p = res.prefill;
          setContact({
            name: p?.contact.name ?? '',
            email: res.email,
            phone: p?.contact.phone ?? '',
            role: p?.contact.role ?? '',
          });
          if (p) {
            setLoc({
              name: p.location.name,
              street: p.location.address.street,
              city: p.location.address.city,
              state: p.location.address.state,
              zip: p.location.address.zip,
              type: p.location.type,
            });
            setUnits(p.chargers.map((c) => ({
              identity: c.identity,
              vendor: c.vendor ?? '',
              model: c.model ?? '',
              connector: c.connector_types[0] ?? 'J1772',
              maxKw: c.max_power_kw != null ? String(c.max_power_kw) : '',
            })));
            // Seed the required choice from the original submission. The
            // submitter can still change it; one registered before the
            // choice existed (null) stays empty and must be picked.
            setConnectivity(prefillConnectivity(p));
          }
        }
      })
      .catch(() => { if (!cancelled) setCheck('error'); });
    return () => { cancelled = true; };
  }, [inviteToken]);

  const setC = (k: keyof typeof contact) => (e: { target: { value: string } }) =>
    setContact((s) => ({ ...s, [k]: e.target.value }));
  const setL = (k: keyof typeof loc) => (e: { target: { value: string } }) =>
    setLoc((s) => ({ ...s, [k]: e.target.value }));
  const setUnit = (i: number, patch: Partial<UnitRow>) =>
    setUnits((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  if (check === 'loading') {
    return (
      <div className={styles.done}>
        <p className={styles.doneSub}>Checking your invite…</p>
      </div>
    );
  }
  if (check === 'error') {
    return (
      <div className={styles.done}>
        <h1 className={styles.doneTitle}>Could not check your invite.</h1>
        <p className={styles.doneSub}>Something went wrong on our side. Reload to try again, or write support@evinitiative.com.</p>
      </div>
    );
  }
  if (!check.valid) {
    const copy = DEAD_LINK_COPY[check.reason] ?? DEAD_LINK_COPY['invalid']!;
    return (
      <div className={styles.done}>
        <h1 className={styles.doneTitle}>{copy.title}</h1>
        <p className={styles.doneSub}>{copy.body}</p>
      </div>
    );
  }

  const isResubmit = !!check.prefill;

  const identitiesValid = units.every((u) => CHARGER_IDENTITY_RE.test(u.identity.trim()));
  const requiredFilled =
    !!contact.name.trim() && !!contact.role
    && !!loc.name.trim() && !!loc.street.trim() && !!loc.city.trim()
    && !!loc.state.trim() && !!loc.zip.trim() && !!loc.type
    && !!connectivity
    && units.length > 0 && identitiesValid;
  const canSubmit = hasTurnstile && !!token && requiredFilled && status !== 'sending';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !token || !inviteToken || !connectivity) return;
    setStatus('sending');
    const body: PublicNetworkSubmission & { connectivity: ChargerConnectivity } = {
      contact: {
        name: contact.name.trim(),
        // Display value only — the server forces the invite's email.
        email: check.email,
        phone: contact.phone.trim() || null,
        role: contact.role,
      },
      location: {
        name: loc.name.trim(),
        address: {
          street: loc.street.trim(),
          city: loc.city.trim(),
          state: loc.state.trim(),
          zip: loc.zip.trim(),
        },
        type: loc.type,
      },
      chargers: units.map((u) => ({
        identity: u.identity.trim(),
        vendor: u.vendor.trim() || null,
        model: u.model.trim() || null,
        connector_types: [u.connector],
        max_power_kw: u.maxKw ? Number(u.maxKw) : null,
      })),
      connectivity,
      turnstile_token: token,
      invite_token: inviteToken,
    };
    try {
      await submitNetworkSubmissionPublic(body);
      setStatus('done');
    } catch {
      setStatus('error');
      setToken(null);
      tsRef.current?.reset();
    }
  };

  if (status === 'done') {
    return (
      <div className={styles.done}>
        <h1 className={styles.doneTitle}>{isResubmit ? 'Submission updated.' : 'Submission received.'}</h1>
        <p className={styles.doneSub}>
          {isResubmit
            ? `Your submission is back with our review team. We reply by email. ${GO_LIVE}`
            : `We review every proposed site personally and reply by email. ${GO_LIVE}`}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.body}>
        <section className={styles.apply}>
          <h2 className={styles.h2}>{isResubmit ? 'Update your submission' : 'Register a location on the network'}</h2>
          <p className={styles.sectionLead}>
            {isResubmit
              ? 'Our team asked for a little more information. Everything you entered is below. Update it and resubmit; the review picks right back up.'
              : `EV Initiative invited you to register this site and its chargers. Tell us about the location and each unit. Our team reviews every submission. ${GO_LIVE}`}
          </p>
          {check.note && (
            <p className={styles.fine}>Note from our team: {check.note}</p>
          )}
          <p className={styles.fine}>
            OCPP server address for each unit:{' '}
            <code>{OCPP_WS_BASE}/&lt;charger identity&gt;</code>{' '}
            <button
              type="button"
              className="eviBtn"
              style={{ padding: '2px 10px', fontSize: 12 }}
              onClick={() => {
                navigator.clipboard?.writeText(OCPP_WS_BASE).then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2000);
                }).catch(() => { /* clipboard blocked — the value is selectable */ });
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            {' '}The URL must end in the unit&rsquo;s exact identity, the same one you enter below.
          </p>
        </section>

        <form className={styles.form} onSubmit={onSubmit}>
          <div className={styles.row}>
            <Field label="Your name" required><input type="text" value={contact.name} onChange={setC('name')} autoComplete="name" /></Field>
            <Field label="Email (from your invite)">
              <input type="email" value={contact.email} readOnly className="clarity-mask" style={{ opacity: 0.7 }} />
            </Field>
          </div>
          <div className={styles.row}>
            <Field label="Phone (optional)"><input type="tel" value={contact.phone} onChange={setC('phone')} autoComplete="tel" /></Field>
            <Field label="Your role" required>
              <select value={contact.role} onChange={setC('role')}>
                <option value="">Select…</option>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
          </div>

          <div className={styles.row}>
            <Field label="Location name" required><input type="text" value={loc.name} onChange={setL('name')} placeholder="e.g. Hillside Apartments" /></Field>
            <Field label="Location type" required>
              <select value={loc.type} onChange={setL('type')}>
                <option value="">Select…</option>
                {LOCATION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Street address" required><input type="text" value={loc.street} onChange={setL('street')} autoComplete="street-address" /></Field>
          <div className={styles.row}>
            <Field label="City" required><input type="text" value={loc.city} onChange={setL('city')} /></Field>
            <Field label="State / Province" required><input type="text" value={loc.state} onChange={setL('state')} /></Field>
          </div>
          <div className={styles.row}>
            <Field label="ZIP / Postal code" required><input type="text" value={loc.zip} onChange={setL('zip')} /></Field>
            <div />
          </div>

          {/* Not inside a Field: its input rule would stretch the checkboxes. */}
          <div role="group" aria-label={CONNECTIVITY_LABEL}
            style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: 'var(--evi-text-mute)' }}>
            <span>{CONNECTIVITY_LABEL}<em className={styles.req}> *</em></span>
            {CHARGER_CONNECTIVITY_LINKS.map((value) => (
              <label key={value} className={styles.terms}>
                <input type="checkbox" name={`connectivity-${value}`} checked={connectivityIncludes(connectivity, value)}
                  onChange={(e) => setConnectivity((d) => connectivityWith(d, value, e.target.checked))} />
                <span>
                  <strong>{CHARGER_CONNECTIVITY_LABELS[value]}</strong>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--evi-text-mute)' }}>{CHARGER_CONNECTIVITY_HINTS[value]}</span>
                </span>
              </label>
            ))}
            <span>Tick both when the site has units on each, or units use both. One answer covers every unit at this location.</span>
          </div>

          {units.map((u, i) => {
            const trimmed = u.identity.trim();
            const badFormat = trimmed.length > 0 && !CHARGER_IDENTITY_RE.test(trimmed);
            return (
              <section key={i} className={styles.section} style={{ marginTop: 4 }}>
                <h2 className={styles.h2} style={{ fontSize: 16 }}>
                  Charger unit {i + 1}
                  {units.length > 1 && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="eviBtn"
                        style={{ padding: '2px 10px', fontSize: 12 }}
                        onClick={() => setUnits((rows) => rows.filter((_, idx) => idx !== i))}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </h2>
                <Field label="Charger identity (exactly as configured on the unit)" required>
                  <input type="text" value={u.identity} onChange={(e) => setUnit(i, { identity: e.target.value })} placeholder="e.g. CU89TVWKPVJ8" />
                </Field>
                {badFormat && (
                  <p className={styles.err}>Letters, numbers, dashes, and underscores only (max 50).</p>
                )}
                <div className={styles.row}>
                  <Field label="Vendor (optional)"><input type="text" value={u.vendor} onChange={(e) => setUnit(i, { vendor: e.target.value })} /></Field>
                  <Field label="Model (optional)"><input type="text" value={u.model} onChange={(e) => setUnit(i, { model: e.target.value })} /></Field>
                </div>
                <div className={styles.row}>
                  <Field label="Connector type" required>
                    <select value={u.connector} onChange={(e) => setUnit(i, { connector: e.target.value })}>
                      <option value="J1772">J1772</option>
                      <option value="NACS">NACS</option>
                      <option value="CCS1">CCS1</option>
                    </select>
                  </Field>
                  <Field label="Max power in kW (optional)">
                    <input type="number" min="0" max="1000" step="0.1" value={u.maxKw} onChange={(e) => setUnit(i, { maxKw: e.target.value })} />
                  </Field>
                </div>
              </section>
            );
          })}
          <button
            type="button"
            className="eviBtn"
            onClick={() => setUnits((rows) => [...rows, { ...EMPTY_UNIT }])}
            disabled={units.length >= 50}
          >
            Add another unit
          </button>

          <ShopTurnstile ref={tsRef} onToken={setToken} />
          {!hasTurnstile && <p className={styles.err}>Verification is unavailable right now. Please try again later.</p>}
          {status === 'error' && (
            <p className={styles.err}>
              Could not submit. Please try again. If it keeps failing, your invite may have expired; write support@evinitiative.com for a fresh link.
            </p>
          )}

          <button type="submit" className="eviBtn eviBtnPrimary" disabled={!canSubmit}>
            {status === 'sending' ? 'Submitting…' : isResubmit ? 'Resubmit for review' : 'Submit for review'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className={styles.field}>
      <span>{label}{required && <em className={styles.req}> *</em>}</span>
      {children}
    </label>
  );
}
